import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaAward,
  FaBell,
  FaBookOpen,
  FaCertificate,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaGraduationCap,
  FaLightbulb,
  FaRobot,
  FaPlay,
  FaSearch,
  FaUserCog,
} from 'react-icons/fa';
import './UniversityGuide.css';

const quickSteps = [
  {
    icon: <FaBookOpen />,
    title: '1. Choose your subjects',
    text: 'Open Select Subjects, search your topic, and build your own flexible combination of subjects.',
  },
  {
    icon: <FaPlay />,
    title: '2. Start an exam',
    text: 'Go to Start Exam, pick a subject and level, then let AI generate your exam when you are ready.',
  },
  {
    icon: <FaClipboardList />,
    title: '3. Answer and submit',
    text: 'Complete the questions before the timer ends. Your progress is saved while you write.',
  },
  {
    icon: <FaAward />,
    title: '4. Review your result',
    text: 'After AI evaluation, check your score, feedback, strengths, weak areas, and certificate eligibility.',
  },
];

const featureCards = [
  {
    icon: <FaGraduationCap />,
    title: 'Dashboard',
    text: 'Your main University home. It shows selected subjects, exams completed, accuracy, certificates, recent exams, and progress.',
  },
  {
    icon: <FaSearch />,
    title: 'Navbar Search',
    text: 'Use the search bar to quickly find subjects, categories, subcategories, or recent exam results.',
  },
  {
    icon: <FaBookOpen />,
    title: 'My Subjects',
    text: 'This is your personal subject list. Add subjects before starting exams so your learning path stays organized.',
  },
  {
    icon: <FaClipboardList />,
    title: 'Exams',
    text: 'Create AI-generated exams by subject and level. If an exam is already active, the app helps you continue it instead of starting a duplicate.',
  },
  {
    icon: <FaRobot />,
    title: 'AI Questions and Evaluation',
    text: 'Questions and evaluation are handled by AI. There is no manual interference in creating questions or checking answers.',
  },
  {
    icon: <FaChartLine />,
    title: 'Analytics',
    text: 'Track overall accuracy, subject performance, strong topics, weak areas, and recent exam trends.',
  },
  {
    icon: <FaCertificate />,
    title: 'Certificates',
    text: 'When you pass an eligible exam, you can generate and view your University certificate from the certificates page.',
  },
];

const examTips = [
  'Select subjects first so the exam page can show your available exam options.',
  'Questions are generated by AI based on the selected subject and exam level.',
  'Read the ready screen before starting. It explains the exam pattern, time, and question count.',
  'Do not refresh or close the page during an exam unless needed. The app saves progress, but staying on the page is safest.',
  'Tab switching may be tracked during exams. Stay focused on the exam screen.',
  'Submit only when you are sure. After submission, AI evaluates your answers and opens your result page.',
];

const navbarItems = [
  'Search: find subjects, categories, subcategories, and recent exams.',
  'Help/Guide: opens this instruction page.',
  'Theme: switch between light and dark dashboard views.',
  'Prepare with Academy: opens learning resources for preparation.',
  'Notifications: opens user notifications and alerts.',
  'Profile menu: edit profile, view subjects, open feedback, settings, about page, and logout.',
];

const UniversityGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="uni-guide-page">
      <header className="uni-guide-header">
        <div className="uni-guide-header-row">
          <button className="uni-guide-back" onClick={() => navigate('/university/dashboard')}>
            <FaArrowLeft /> Back to Dashboard
          </button>
          <span className="uni-guide-kicker">ULSAAPP University Guide</span>
        </div>
        <div className="uni-guide-title-wrap">
          <h1>How to use the Virtual University</h1>
          <p>
            This guide explains the Virtual University system: choose your own subjects, take
            AI-generated exams, receive AI evaluation, and earn certificates based on performance.
          </p>
        </div>
      </header>

      <main className="uni-guide-main">
        <section className="uni-guide-panel uni-guide-start">
          <div>
            <FaLightbulb className="uni-guide-panel-icon" />
            <h2>Start Here</h2>
            <p>
              The Virtual University focuses mainly on evaluation and certification, not regular
              classes or fixed syllabi. You can prepare through Academy, books, YouTube, courses,
              or outside resources, then prove your knowledge through AI-generated University exams.
            </p>
          </div>
          <button className="uni-guide-primary" onClick={() => navigate('/university/subjects')}>
            Choose Subjects
          </button>
        </section>

        <section className="uni-guide-section">
          <div className="uni-guide-section-heading">
            <h2>Simple Learning Path</h2>
            <p>Follow these four steps when you are new to the University dashboard.</p>
          </div>
          <div className="uni-guide-step-grid">
            {quickSteps.map((step) => (
              <article className="uni-guide-step-card" key={step.title}>
                <div className="uni-guide-card-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="uni-guide-section">
          <div className="uni-guide-ai-panel">
            <div className="uni-guide-ai-icon">
              <FaRobot />
            </div>
            <div>
              <h2>Fully AI-Generated Exams and AI Evaluation</h2>
              <p>
                In this Virtual University system, the questions are generated by AI and the
                evaluation is also done by AI after submission. There is no manual interference
                in question creation or answer checking. The platform is designed to evaluate
                what a student knows and issue certification based on exam performance.
              </p>
              <p>
                Unlike a traditional university model, this system does not depend on regular
                classes, fixed batches, or a fixed syllabus for every learner. Students have the
                flexibility to choose their own combination of subjects and build a personal
                evaluation path.
              </p>
            </div>
          </div>
        </section>

        <section className="uni-guide-section">
          <div className="uni-guide-section-heading">
            <h2>Main Features</h2>
            <p>These are the important pages and what each one is used for.</p>
          </div>
          <div className="uni-guide-feature-grid">
            {featureCards.map((feature) => (
              <article className="uni-guide-feature-card" key={feature.title}>
                <div className="uni-guide-card-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="uni-guide-two-column">
          <article className="uni-guide-panel">
            <h2>Before Taking an Exam</h2>
            <ul className="uni-guide-list">
              {examTips.map((tip) => (
                <li key={tip}>
                  <FaCheckCircle />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="uni-guide-panel">
            <h2>Navbar and Profile Menu</h2>
            <ul className="uni-guide-list">
              {navbarItems.map((item) => (
                <li key={item}>
                  <FaCheckCircle />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>

        <section className="uni-guide-section">
          <div className="uni-guide-section-heading">
            <h2>Frequently Needed Actions</h2>
            <p>Use these shortcuts when you know what you want to do next.</p>
          </div>
          <div className="uni-guide-action-row">
            <button onClick={() => navigate('/university/my-subjects')}>
              <FaBookOpen /> My Subjects
            </button>
            <button onClick={() => navigate('/university/exam')}>
              <FaPlay /> Start Exam
            </button>
            <button onClick={() => navigate('/university/exam-history')}>
              <FaClipboardList /> Exam History
            </button>
            <button onClick={() => navigate('/university/analytics')}>
              <FaChartLine /> Analytics
            </button>
            <button onClick={() => navigate('/university/certificates')}>
              <FaCertificate /> Certificates
            </button>
            <button onClick={() => navigate('/notifications')}>
              <FaBell /> Notifications
            </button>
            <button onClick={() => navigate('/account-settings')}>
              <FaUserCog /> Account Settings
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default UniversityGuide;
